import { useEffect } from "react";
import {
  Badge,
  Button,
  Icon,
  TextAreaField,
} from "@elaniintc/goes-design-system";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type {
  UnlinkAssociationFormData,
  UnlinkAssociationModalProps,
} from "./types";

/** Max length for the optional "motivo" field (backend contract: max 500). */
const MAX_REASON_LENGTH = 500;

/**
 * Mini-form modal to unlink an association (JER-453).
 * The parent owns the open state and the mutation; this component only handles
 * the form + confirm/cancel.
 */
export const UnlinkAssociationModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  title,
  description,
  person,
}: Readonly<UnlinkAssociationModalProps>) => {
  const { register, handleSubmit, watch, reset } =
    useForm<UnlinkAssociationFormData>({
      defaultValues: { reason: "" },
    });

  // Clear the field each time the modal is (re)opened for another association.
  useEffect(() => {
    if (isOpen) reset({ reason: "" });
  }, [isOpen, reset]);

  const reason = watch("reason") ?? "";

  const associatedSince = person.associatedSince
    ? dayjs(person.associatedSince).format("DD/MM/YYYY")
    : null;

  const onSubmit = (data: UnlinkAssociationFormData) => {
    onConfirm(data.reason.trim());
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        showCloseButton={false}
        className="max-w-[520px] w-full gap-8 rounded-xl border-0"
      >
        <DialogHeader>
          <DialogTitle className="text-center !text-h2 font-semibold text-heading tracking-[-0.02em]">
            {title}
          </DialogTitle>
          <DialogDescription className="text-center !text-base text-body leading-3xl font-normal">
            {description}
          </DialogDescription>
        </DialogHeader>

        {/* Person being unlinked */}
        <div className="border border-neutral-quiet rounded-lg overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="size-11 rounded-full bg-surface-action-accent-quiet border-[0.5px] border-action-quiet flex items-center justify-center shrink-0">
              <Icon
                type="fas"
                name="user"
                className="text-sm text-action-quiet"
              />
            </div>
            <div className="min-w-0">
              <p className="text-base font-semibold text-body-loud truncate">
                {person.name}
              </p>
              <Badge size="xs" variant="success">
                {person.statusLabel}
              </Badge>
            </div>
          </div>
          {(person.email || associatedSince) && (
            <div className="flex flex-col gap-2 px-4 py-3 border-t border-neutral-quiet bg-neutral-object-accent-silent">
              {person.email && (
                <div className="flex items-center gap-2">
                  <div className="size-7 rounded-lg bg-neutral-object-accent-silent border-[0.5px] border-neutral flex items-center justify-center shrink-0">
                    <Icon
                      type="far"
                      name="envelope"
                      className="text-sm text-body-quiet"
                    />
                  </div>
                  <p className="text-sm text-body truncate">{person.email}</p>
                </div>
              )}
              {associatedSince && (
                <div className="flex items-center gap-2">
                  <div className="size-7 rounded-lg bg-neutral-object-accent-silent border-[0.5px] border-neutral flex items-center justify-center shrink-0">
                    <Icon
                      type="far"
                      name="calendar-check"
                      className="text-sm text-body-quiet"
                    />
                  </div>
                  <p className="text-sm text-body">
                    Asociado desde {associatedSince}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Reason + actions */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
          <TextAreaField
            id="reason"
            label="Motivo de desvinculación"
            placeholder="Ingrese aquí el motivo por el cual desea desvincular al usuario."
            count={reason.length}
            limit={MAX_REASON_LENGTH}
            maxLength={MAX_REASON_LENGTH}
            {...register("reason")}
          />
          <div className="w-full grid grid-cols-2 gap-3">
            {/* Accept first in the DOM to receive focus, visually placed second. */}
            <Button
              data-testid="unlink-confirm-button"
              size="lg"
              type="submit"
              variant="default"
              disabled={isLoading}
              className="w-full rounded-lg cursor-pointer order-2"
            >
              Desvincular
              {isLoading && (
                <Icon
                  type="far"
                  name="spinner-third"
                  className="text-current animate-spin"
                />
              )}
            </Button>
            <Button
              data-testid="unlink-cancel-button"
              size="lg"
              type="button"
              variant="tertiary"
              onClick={onClose}
              className="w-full rounded-lg cursor-pointer order-1"
            >
              Cancelar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
